import styled from 'styled-components';
import { InfoIcon } from '../../assets/icons/InfoIcon';
import { CheckmarkCircleIcon } from '../../assets/icons/CheckmarkCircleIcon';
import { WarningIcon } from '../../assets/icons/WarningIcon';
import theme from '../../theme/light-theme';
import { CloseIcon } from '../../assets/icons/CloseIcon';

export type Severity = 'error' | 'warning' | 'action' | 'success';

interface Props {
  children: React.ReactNode;
  severity: Severity;
  height?: string;
  width?: string;
  margin?: string;
  onClose?: () => void;
  padding?: string;
  /** Overrides the border the severity would give the alert, to soften it. */
  borderColor?: string;
  /** Only has an effect on an alert with a width wider than its content. */
  justifyContent?: string;
}

const getSeverityElements = (
  severity: Severity
): { icon: JSX.Element; color: string; backgroundColor?: string; borderColor?: string } => {
  switch (severity) {
    case 'error':
      return {
        icon: <WarningIcon size="18px" color={theme.palette.functions.error.primary} />,
        color: theme.palette.functions.error.primary,
        backgroundColor: theme.palette.functions.error.tertiary
      };
    case 'action':
      return {
        icon: <InfoIcon size="18px" color={theme.palette.functions.action.primary} />,
        color: theme.palette.functions.action.secondary,
        backgroundColor: theme.palette.functions.action.tertiary
      };
    case 'warning':
      return {
        icon: <WarningIcon size="18px" color={theme.palette.functions.warning.primary} />,
        color: theme.palette.functions.warning.secondary,
        backgroundColor: theme.palette.functions.warning.tertiary,
        borderColor: theme.palette.functions.warning.primary
      };
    case 'success':
      return {
        icon: <CheckmarkCircleIcon size="18px" color={theme.palette.functions.success.primary} />,
        color: theme.palette.functions.success.secondary,
        backgroundColor: theme.palette.functions.success.tertiary
      };
  }
};

const Alert = (props: Props) => {
  const { children, severity, height, width, margin, onClose, padding, borderColor, justifyContent } = props;

  const { color, backgroundColor, borderColor: severityBorderColor, icon } = getSeverityElements(severity);

  return (
    <Container
      color={color}
      backgroundColor={backgroundColor}
      borderColor={borderColor ?? severityBorderColor}
      height={height}
      width={width}
      margin={margin}
      data-testid="success-alert"
      padding={padding}
      justifyContent={justifyContent}
    >
      <IconColumn>{icon}</IconColumn>
      <Column>{children}</Column>
      {onClose && (
        <CloseContainer onClick={onClose}>
          <CloseIcon color={color} size="14px" />
        </CloseContainer>
      )}
    </Container>
  );
};

interface ContainerProps {
  color: string;
  backgroundColor?: string;
  borderColor?: string;
  height?: string;
  width?: string;
  top?: string;
  bottom?: string;
  margin?: string;
  padding?: string;
  justifyContent?: string;
}

const Container = styled.div<ContainerProps>`
  display: flex;
  align-items: center;
  ${(props) => props.justifyContent && `justify-content: ${props.justifyContent};`}
  color: ${(props) => props.color};
  background-color: ${(props) => props.backgroundColor};
  height: ${(props) => props.height};
  width: ${(props) => props.width};
  column-gap: 12px;
  border: 1px solid ${(props) => props.borderColor ?? props.color};
  border-radius: 8px;
  margin: ${(props) => props.margin ?? ''};
  padding: ${(props) => props.padding ?? '0px 12px'};
`;

const CloseContainer = styled.div`
  right: 0px;
  top: 0px;
  width: 24px;
  height: 48px;
  display: flex;
  justify-content: right;
  align-items: center;
`;

const Column = styled.div``;

// The icon is an inline svg, so it would otherwise sit on the text baseline with the
// descender space below it and read as vertically off centre.
const IconColumn = styled.div`
  display: flex;
  align-items: center;
`;

export default Alert;
